/**
 * useFinanceCounts — single hook feeding tab badges across the Finance domain.
 * Derives every count from canonical signals; never persists.
 */
import { useMemo } from "react";
import { useAppData } from "@/data/AppDataContext";
import { usePayRunViews } from "@/data/finance/payRun/usePayRunViews";
import { useVendorPO } from "@/data/VendorPOContext";
import { getPendingFieldAdds } from "@/data/finance/payRun/fieldAdds";
import { deriveAllHoldbackStates } from "@/data/contractor/networkExecution/holdback/deriveState";
import { deriveAllDisputes } from "@/data/contractor/networkExecution/dispute/dispute";
import {
  buildApprovalsInbox,
  buildCollectionsRows,
  buildBillsFromPOs,
  buildPeriodClose,
} from "@/data/finance/financeWorkbenchSelectors";

const MS_DAY = 86_400_000;

export function useFinanceCounts() {
  const appData = useAppData();
  const { jobs, holdbackLedger, disputeEvents } = appData;
  const fieldAddSubmissions: any[] = (appData as any).fieldAddSubmissions ?? [];
  const payRunRecords: any[] = (appData as any).payRunRecords ?? [];
  const laborActualRecords: any[] = (appData as any).laborActualRecords ?? [];
  const { heldRows } = usePayRunViews();
  const { purchaseOrders } = useVendorPO();

  return useMemo(() => {
    const pendingFieldAdds = getPendingFieldAdds(fieldAddSubmissions);
    const holdbackStates = deriveAllHoldbackStates(holdbackLedger);
    const disputeStates = deriveAllDisputes(disputeEvents);

    const approvals = buildApprovalsInbox({
      heldRows, pendingFieldAdds, holdbackStates, disputeStates, jobs,
    });
    const collections = buildCollectionsRows(jobs);
    const bills = buildBillsFromPOs(purchaseOrders);
    const close = buildPeriodClose({
      jobs, payRunRecords, heldRows, pendingFieldAdds, holdbackStates, disputeStates, bills,
    });

    // PO attention = awaiting-bill + eta-unconfirmed + disputed + stale draft (>7d).
    let poAttention = 0;
    for (const p of purchaseOrders) {
      if (p.finance?.paymentState === "paid" || p.finance?.closeState === "closed") continue;
      if (p.status === "Delivered") poAttention += 1;
      else if (p.status === "Submitted" && !p.etaConfirmed) poAttention += 1;
      else if (p.finance?.disputed === true) poAttention += 1;
      else if (p.status === "Draft" && (Date.now() - new Date(p.createdAt).getTime()) > 7 * MS_DAY) poAttention += 1;
    }

    // Payroll = labor actuals captured in last 14d (typical pay period).
    const payrollWindow = Date.now() - 14 * MS_DAY;
    const payrollRecords = laborActualRecords.filter(
      (r) => new Date(r.enteredAt).getTime() >= payrollWindow,
    ).length;

    return {
      approvalsCount: approvals.length,
      approvalsCritical: approvals.filter((a) => a.urgency === "critical").length,
      collectionsCount: collections.filter((c) => c.oldestDueDays > 0).length,
      collectionsOverdue: collections.filter((c) => c.oldestDueDays > 30).length,
      billsCount: bills.length,
      billsOverdue: bills.filter((b) => b.status === "overdue").length,
      closeOpenCount: close.checks.filter((c) => c.status !== "complete").length,
      poAttention,
      payrollRecords,
    };
  }, [jobs, holdbackLedger, disputeEvents, fieldAddSubmissions, payRunRecords, heldRows, purchaseOrders, laborActualRecords]);
}

